/**
 * Analytics Dashboard Component
 * Displays revenue, sales, and conversion metrics
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../../services/api';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import {
  DollarSign,
  TrendingUp,
  Users,
  RotateCcw,
  RefreshCw,
  Package,
  ShoppingCart,
  CreditCard,
  BarChart3,
  PieChart,
  HelpCircle,
  Trophy,
  AlertCircle,
} from 'lucide-react';

type Period = 'today' | '7d' | '30d' | '90d' | 'all';

interface AnalyticsData {
  period: string;
  dateRange: {
    start: string;
    end: string;
  };
  revenue: {
    total: number;
    byCategory: Record<string, number>;
    averageOrderValue: number;
  };
  orders: {
    total: number;
    completed: number;
    refunded: number;
    byStatus: Record<string, number>;
    refundRate: number;
  };
  conversion: {
    totalUsers: number;
    conversionRate: number;
  };
  topProducts: Array<{
    productId: string;
    title: string;
    revenue: number;
    count: number;
  }>;
  paymentProviders: Record<string, number>;
}

const periodLabels: Record<Period, string> = {
  today: 'Today',
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '90 Days',
  all: 'All Time',
};

export default function Analytics() {
  const [period, setPeriod] = useState<Period>('30d');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const analyticsData = await adminApi.getAnalytics(period);
      setData(analyticsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Calculate stats for the widget
  const statItems: StatItem[] = useMemo(() => {
    if (!data) return [];
    return [
      {
        id: 'revenue',
        label: 'Total Revenue',
        value: formatCurrency(data.revenue.total),
        icon: DollarSign,
        variant: 'success',
      },
      {
        id: 'aov',
        label: 'Avg Order Value',
        value: formatCurrency(data.revenue.averageOrderValue),
        icon: ShoppingCart,
        variant: 'primary',
      },
      {
        id: 'conversion',
        label: 'Conversion Rate',
        value: formatPercent(data.conversion.conversionRate),
        icon: TrendingUp,
        variant: 'primary',
      },
      {
        id: 'refund',
        label: 'Refund Rate',
        value: formatPercent(data.orders.refundRate),
        icon: RotateCcw,
        variant: data.orders.refundRate > 5 ? 'warning' : 'default',
      },
    ];
  }, [data]);

  // Format category name
  const formatCategory = (category: string): string => {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Status color mapping
  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
      processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      refunded: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      failed: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    };
    return colors[status] || colors.pending;
  };

  // Provider icon mapping
  const getProviderIcon = (provider: string): string => {
    const icons: Record<string, string> = {
      stripe: '💳',
      paypal: '🅿️',
      braintree: '🌳',
    };
    return icons[provider.toLowerCase()] || '💰';
  };

  if (error) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Analytics
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Platform performance metrics and insights
            </p>
          </div>
        </div>

        {/* Error State */}
        <div className="card p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Failed to Load Analytics
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadAnalytics}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Analytics
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Platform performance metrics and insights
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          disabled={loading}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Period Selector */}
      <section className="card p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Time Period:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(periodLabels) as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                disabled={loading}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === p
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>
          {data && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {new Date(data.dateRange.start).toLocaleDateString()} - {new Date(data.dateRange.end).toLocaleDateString()}
            </div>
          )}
        </div>
      </section>

      {/* Key Metrics Stats */}
      <section>
        <StatsWidget stats={statItems} columns={4} loading={loading} />
      </section>

      {/* Secondary Stats Row */}
      {data && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 text-center">
            <ShoppingCart size={24} className="mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {data.orders.total}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Orders</p>
          </div>
          <div className="card p-4 text-center">
            <Package size={24} className="mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {data.orders.completed}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
          </div>
          <div className="card p-4 text-center">
            <Users size={24} className="mx-auto text-purple-500 mb-2" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {data.conversion.totalUsers}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Users</p>
          </div>
          <div className="card p-4 text-center">
            <RotateCcw size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {data.orders.refunded}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Refunded</p>
          </div>
        </section>
      )}

      {/* Revenue by Category */}
      <section className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <PieChart size={20} className="text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Revenue by Category
          </h3>
        </div>
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="flex justify-between mb-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/6" />
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              </div>
            ))}
          </div>
        ) : data && Object.keys(data.revenue.byCategory).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(data.revenue.byCategory)
              .sort(([, a], [, b]) => b - a)
              .map(([category, revenue]) => {
                const percentage = data.revenue.total > 0 ? (revenue / data.revenue.total) * 100 : 0;
                return (
                  <div key={category}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {formatCategory(category)}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {percentage.toFixed(1)}%
                        </span>
                        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                          {formatCurrency(revenue)}
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="text-center py-8">
            <PieChart size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-gray-500 dark:text-gray-400">
              No revenue data available for this period
            </p>
          </div>
        )}
      </section>

      {/* Top Products */}
      <section className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={20} className="text-amber-500" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Top Products by Revenue
          </h3>
        </div>
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full" />
                <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        ) : data && data.topProducts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Sales
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.topProducts.map((product, index) => (
                  <tr
                    key={product.productId}
                    className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                        index === 0
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          : index === 1
                          ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                          : index === 2
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {product.title}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {product.count}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(product.revenue)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Package size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-gray-500 dark:text-gray-400">
              No products sold in this period
            </p>
          </div>
        )}
      </section>

      {/* Order Status & Payment Providers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Order Status Distribution */}
        <section className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart size={20} className="text-blue-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Orders by Status
            </h3>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex justify-between animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                  <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
                </div>
              ))}
            </div>
          ) : data ? (
            <div className="space-y-3">
              {Object.entries(data.orders.byStatus).map(([status, count]) => (
                <div key={status} className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                    {status}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
                    {count}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        {/* Payment Providers */}
        <section className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={20} className="text-green-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Payment Providers
            </h3>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex justify-between animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                  <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
                </div>
              ))}
            </div>
          ) : data ? (
            <div className="space-y-3">
              {Object.entries(data.paymentProviders).map(([provider, count]) => (
                <div key={provider} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span>{getProviderIcon(provider)}</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {provider}
                    </span>
                  </div>
                  <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-full text-sm font-medium">
                    {count}
                  </span>
                </div>
              ))}
              {Object.keys(data.paymentProviders).length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No payment data available
                </p>
              )}
            </div>
          ) : null}
        </section>
      </div>

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help understanding metrics?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn about analytics, reporting, and data insights
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}
